import { v4 as uuidv4 } from 'uuid';

export const handleImageUpload = (
  productIndex: number, 
  files: FileList, 
  products: any[], 
  setProducts: React.Dispatch<React.SetStateAction<any[]>>
) => {
  const newProducts = [...products];
  const newImages = Array.from(files).map(file => ({
    file,
    preview: URL.createObjectURL(file)
  }));
  
  newProducts[productIndex] = {
    ...newProducts[productIndex],
    images: [...newProducts[productIndex].images, ...newImages]
  };
  setProducts(newProducts);
};

export const removeImage = (
  productIndex: number, 
  imageIndex: number, 
  products: any[], 
  setProducts: React.Dispatch<React.SetStateAction<any[]>>
) => {
  const newProducts = [...products];
  const product = newProducts[productIndex];
  
  URL.revokeObjectURL(product.images[imageIndex].preview);
  
  product.images = product.images.filter((_, index) => index !== imageIndex);
  setProducts(newProducts);
};

// Banner dimensions constants
export const BANNER_ASPECT_RATIO = 3 / 1; // 3:1 aspect ratio
export const RECOMMENDED_BANNER_WIDTH = 1200;
export const RECOMMENDED_BANNER_HEIGHT = 400;

/**
 * Creates a cropped image from an HTML image element based on crop parameters
 * @param image The source image element
 * @param crop The crop parameters
 * @returns A promise that resolves to a Blob of the cropped image
 */
export const createCroppedImage = async (
  image: HTMLImageElement,
  crop: { x: number; y: number; width: number; height: number }
): Promise<Blob> => {
  const canvas = document.createElement('canvas');
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  
  // Set canvas dimensions to the cropped size
  canvas.width = crop.width * scaleX;
  canvas.height = crop.height * scaleY;
  
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get canvas context');
  }
  
  // Draw the cropped area onto the canvas
  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width * scaleX,
    crop.height * scaleY
  );
  
  // Convert canvas to blob
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Canvas to Blob conversion failed'));
    }, 'image/jpeg', 0.95);
  });
};

/**
 * Optimizes an image by resizing and compressing it before upload
 * @param file Original image file
 * @param maxWidth Maximum width in pixels (maintains aspect ratio)
 * @param quality Compression quality (0-1)
 * @returns Promise resolving to an optimized File object
 */
export const optimizeImage = async (
  file: File, 
  maxWidth: number = 1200, 
  quality: number = 0.8
): Promise<File> => {
  return new Promise((resolve, reject) => {
    // Skip optimization for small images (under 500KB)
    if (file.size < 500 * 1024) {
      console.log(`Image ${file.name} is already small (${Math.round(file.size / 1024)}KB), skipping optimization`);
      return resolve(file);
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          const ratio = maxWidth / width;
          width = maxWidth;
          height = height * ratio;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress image
        ctx?.drawImage(img, 0, 0, width, height);
        
        // Convert to file
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas to Blob conversion failed'));
              return;
            }
            
            // Use original file name but ensure it's a jpeg
            const fileName = file.name.replace(/\.[^/.]+$/, "") + ".jpg";
            
            const optimizedFile = new File(
              [blob],
              fileName,
              { type: 'image/jpeg', lastModified: Date.now() }
            );
            
            console.log(`Optimized ${file.name}: ${Math.round(file.size / 1024)}KB → ${Math.round(optimizedFile.size / 1024)}KB`);
            resolve(optimizedFile);
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
};

/**
 * Verifies that a file is a valid image and meets size requirements
 * @param file The file to verify
 * @param maxSizeMB Maximum file size in MB
 * @returns Promise resolving to true if valid, rejects with error if invalid
 */
export const verifyImage = (file: File, maxSizeMB: number = 10): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    // Check file size
    if (file.size > maxSizeMB * 1024 * 1024) {
      reject(new Error(`Image size exceeds ${maxSizeMB}MB limit`));
      return;
    }
    
    // Verify it's actually an image
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => reject(new Error('Invalid image file'));
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Generates a unique file path for storing an image
 * @param userId User ID
 * @param serviceId Service ID
 * @param fileName Original file name
 * @returns A sanitized, unique path string
 */
export const generateImagePath = (userId: string, serviceId: string, fileName: string): string => {
  // Sanitize the filename by removing special characters and spaces
  const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
  
  // Create a unique ID to prevent collisions
  const uniqueId = uuidv4().substring(0, 8);
  
  // Use timestamp for ordering
  const timestamp = Date.now();
  
  return `${userId}/${serviceId}/${timestamp}-${uniqueId}-${sanitizedFileName}`;
};

/**
 * Safely extract the file extension from a filename
 * @param fileName Original file name
 * @returns The file extension (lowercase, without the dot)
 */
export const getFileExtension = (fileName: string): string => {
  const match = fileName.match(/\.([^.]+)$/);
  return match ? match[1].toLowerCase() : 'jpg';
};
